import {
  type NormRanges,
  type NormRangeSettings,
  getGenderAdjustedRanges,
  calculateWeightRange,
  calculateFatMassRange,
  calculateMuscleMassRange,
  calculateBodyWaterRange,
  calculateFatFreeMassPercentRange,
  calculateBMIRange,
} from "../types/normRanges";

const STORAGE_KEY = "withings_norm_ranges";

type StatusValue = "below_range" | "elevated" | "normal" | "unknown";

export const NormRangeService = {
  /**
   * Gibt die aktuellen Normbereiche zurück (mit Geschlecht und individuellen Anpassungen)
   *
   * @returns {NormRanges} Current norm ranges with gender adjustments
   *
   */
  getCurrentNormRanges(): NormRanges {
    const settings = this.loadSettings();
    const baseRanges = getGenderAdjustedRanges(settings.gender);

    // Gewicht basierend auf Größe und Geschlecht berechnen
    const weightRange = calculateWeightRange(settings.height, settings.gender);

    // Abgeleitete Bereiche basierend auf Gewicht und benutzerdefinierten Werten berechnen
    const customBodyFatRange = settings.customRanges.bodyFat;
    const customMuscleMassRange = settings.customRanges.muscleMass;
    const customBodyWaterRange = settings.customRanges.bodyWater;

    const fatMassRange = calculateFatMassRange(weightRange, settings.gender, customBodyFatRange);
    const muscleMassRange = calculateMuscleMassRange(
      weightRange,
      settings.gender,
      customMuscleMassRange,
    );
    const bodyWaterRange = calculateBodyWaterRange(
      weightRange,
      settings.gender,
      customBodyWaterRange,
    );
    const fatFreeMassPercentRange = calculateFatFreeMassPercentRange(
      weightRange,
      settings.gender,
      customBodyFatRange,
    );
    const bmiRange = calculateBMIRange();

    // Individuelle Anpassungen anwenden
    const customRanges = settings.customRanges;

    return {
      ...baseRanges,
      bmi: bmiRange,
      bodyWater: bodyWaterRange,
      fatFreeMassPercent: fatFreeMassPercentRange,
      fatMass: fatMassRange,
      muscleMass: muscleMassRange,
      weight: weightRange,
      ...customRanges,
    };
  },

  /**
   * Returns the background color for the status
   *
   * @param status - Status value
   *
   * @returns {string} Background color class name
   *
   */
  getStatusBgColor(status: StatusValue): string {
    switch (status) {
      case "normal": {
        return "bg-green-500/20";
      }
      case "elevated": {
        return "bg-red-500/20";
      }
      case "below_range": {
        return "bg-yellow-500/20";
      }
      default: {
        return "bg-slate-500/20";
      }
    }
  },

  /**
   * Returns the color for the status
   *
   * @param status - Status value
   *
   * @returns {string} Text color class name
   *
   */
  getStatusColor(status: StatusValue): string {
    switch (status) {
      case "normal": {
        return "text-green-400";
      }
      case "elevated": {
        return "text-red-400";
      }
      case "below_range": {
        return "text-yellow-400";
      }
      default: {
        return "text-slate-400";
      }
    }
  },

  /**
   * Returns the status of a value (normal, elevated, below range)
   *
   * @param value - Value to check
   *
   * @param metric - Metric key
   *
   * @returns {"below_range" | "elevated" | "normal" | "unknown"} Status value
   *
   */
  getValueStatus(value: number, metric: keyof NormRanges): StatusValue {
    const ranges = this.getCurrentNormRanges();
    const range = ranges[metric];

    // Reason: TypeScript can't infer that ranges[metric] is always defined, but it is per NormRanges interface
    if (value < range.min) {
      return "below_range";
    }
    if (value > range.max) {
      return "elevated";
    }
    return "normal";
  },

  /**
   * Prüft, ob ein Wert im Normbereich liegt
   *
   * @param value - Value to check
   *
   * @param metric - Metric key
   *
   * @returns {boolean} True if value is within norm range
   *
   */
  isInNormRange(value: number, metric: keyof NormRanges): boolean {
    const ranges = this.getCurrentNormRanges();
    const range = ranges[metric];

    // Reason: TypeScript can't infer that ranges[metric] is always defined, but it is per NormRanges interface
    return value >= range.min && value <= range.max;
  },

  /**
   * Lädt die gespeicherten Normbereich-Einstellungen
   *
   * @returns {NormRangeSettings} Loaded settings or defaults
   *
   */
  loadSettings(): NormRangeSettings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as NormRangeSettings;
      }
    } catch {
      // NOTE: Fehler beim Laden der Normbereich-Einstellungen
    }

    // Standard-Einstellungen
    return {
      age: 30,
      customRanges: {},
      gender: "male",
      height: 175,
    };
  },

  /**
   * Setzt die Standard-Einstellungen zurück
   *
   * @returns {NormRangeSettings} Default settings
   *
   */
  resetToDefaults(): NormRangeSettings {
    const defaultSettings: NormRangeSettings = {
      age: 30,
      customRanges: {},
      gender: "male",
      height: 175,
    };

    this.saveSettings(defaultSettings);
    return defaultSettings;
  },

  /**
   * Speichert die Normbereich-Einstellungen
   *
   * @param settings
   *
   */
  saveSettings(settings: NormRangeSettings): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // NOTE: Fehler beim Speichern der Normbereich-Einstellungen
    }
  },

  /**
   * Aktualisiert einzelne Einstellungen
   *
   * @param updates - Partial settings to update
   *
   * @returns {NormRangeSettings} Updated settings
   *
   */
  updateSettings(updates: Partial<NormRangeSettings>): NormRangeSettings {
    const current = this.loadSettings();
    const updated = { ...current, ...updates };
    this.saveSettings(updated);
    return updated;
  },
};
